// src/pages/DashboardModern.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package,
  Star,
  Clock,
  RefreshCw,
  Bell,
  ChevronRight,
  MapPin
} from 'lucide-react';
import { toast } from 'react-toastify';

// Components
import BottomNav from '../components/common/BottomNav';
import AppLogo from '../components/common/AppLogo';
import StatusToggle from '../components/common/StatusToggle';
import DeliveryMap from '../components/map/DeliveryMap';
import MissionCard from '../components/missions/MissionCard';
import StatsCard from '../components/stats/StatsCard';

// Store
import {
  fetchMyProfile,
  fetchMyStats,
  fetchCurrentDeliveries,
  updateMyStatus,
  selectDriverProfile,
  selectDriverStats,
  selectCurrentDeliveries,
  selectDriverLoading
} from '../store/slices/driverSlice';

// Hooks
import useGeolocation from '../hooks/useGeolocation';

const DashboardModern = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State
  const [refreshing, setRefreshing] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  // Selectors
  const profile = useSelector(selectDriverProfile);
  const stats = useSelector(selectDriverStats);
  const currentDeliveries = useSelector(selectCurrentDeliveries);
  const loading = useSelector(selectDriverLoading);

  // GPS Hook
  const { position, error: gpsError } = useGeolocation({
    enableHighAccuracy: true,
    watchPosition: profile?.is_available
  });

  // Load data on mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Auto-refresh when online
  useEffect(() => {
    if (!profile?.is_available) return;

    const interval = setInterval(() => {
      refreshData();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [profile?.is_available]);

  const loadDashboardData = async () => {
    try {
      await Promise.all([
        dispatch(fetchMyProfile()).unwrap(),
        dispatch(fetchMyStats('30d')).unwrap(),
        dispatch(fetchCurrentDeliveries()).unwrap()
      ]);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Erreur lors du chargement');
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        dispatch(fetchCurrentDeliveries()).unwrap(),
        dispatch(fetchMyStats('30d')).unwrap()
      ]);
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleStatusToggle = async () => {
    setStatusLoading(true);
    try {
      const newStatus = profile?.driver_status === 'online' ? 'offline' : 'online';
      await dispatch(updateMyStatus(newStatus)).unwrap();
      toast.success(newStatus === 'online' ? 'Vous êtes en ligne!' : 'Vous êtes hors ligne');
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleMissionClick = (mission) => {
    navigate(`/driver/mission/${mission.id}`);
  };

  // Calculate today's stats
  const todayStats = {
    deliveries: stats?.today_deliveries || 0,
    rating: stats?.driver_rating || profile?.performance?.driver_rating || 0,
    acceptanceRate: stats?.acceptance_rate || profile?.performance?.acceptance_rate || 100
  };

  const isOnline = profile?.driver_status === 'online';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AppLogo size={40} />
              <div>
              <h1 className="text-xl font-bold text-gray-900">
                {getGreeting()}, {profile?.first_name || 'Livreur'}
              </h1>
              <p className="text-sm text-gray-500">
                {new Date().toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long'
                })}
              </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={refreshData}
                disabled={refreshing}
                className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center"
              >
                <RefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  2
                </span>
              </motion.button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-4 pb-24 md:pb-4">
        {/* Status Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <StatusToggle
            isOnline={isOnline}
            onToggle={handleStatusToggle}
            isLoading={statusLoading}
            todayDeliveries={todayStats.deliveries}
          />
        </motion.div>

        {/* Map Section */}
        <AnimatePresence>
          {isOnline && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: mapExpanded ? 400 : 200 }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-6"
            >
              <DeliveryMap
                driverPosition={position ? [position.latitude, position.longitude] : null}
                currentDelivery={currentDeliveries?.[0]}
                onDeliveryClick={handleMissionClick}
                isExpanded={mapExpanded}
                onToggleExpand={() => setMapExpanded(!mapExpanded)}
                className="h-full"
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <div className="grid grid-cols-3 gap-3">
            <StatsCard
              icon={Package}
              label="Livraisons"
              value={todayStats.deliveries}
              subValue="aujourd'hui"
              color="emerald"
              delay={0}
            />
            <StatsCard
              icon={Star}
              label="Note"
              value={todayStats.rating.toFixed(1)}
              subValue="/ 5"
              color="orange"
              delay={0.1}
            />
            <StatsCard
              icon={Clock}
              label="Taux"
              value={`${todayStats.acceptanceRate}%`}
              subValue="acceptation"
              color="purple"
              delay={0.2}
            />
          </div>
        </motion.div>

        {/* Current Deliveries */}
        {currentDeliveries && currentDeliveries.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">
                Mes livraisons ({currentDeliveries.length})
              </h2>
              <button
                onClick={() => navigate('/driver/missions')}
                className="text-sm text-emerald-600 font-medium flex items-center gap-1"
              >
                Voir tout
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              {currentDeliveries.slice(0, 2).map((delivery, index) => (
                <MissionCard
                  key={delivery.id}
                  mission={delivery}
                  variant="current"
                  onView={handleMissionClick}
                  onNavigate={(m) => {
                    // Open in maps app
                    const url = `https://www.google.com/maps/dir/?api=1&destination=${m.delivery_latitude},${m.delivery_longitude}`;
                    window.open(url, '_blank');
                  }}
                  onComplete={(m) => navigate(`/driver/mission/${m.id}`)}
                  onCall={(phone) => window.open(`tel:${phone}`)}
                  index={index}
                />
              ))}
            </div>
          </motion.section>
        )}

        {/* Empty state: en ligne mais aucune livraison assignée pour le moment */}
        {isOnline && (!currentDeliveries || currentDeliveries.length === 0) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucune livraison assignée
            </h3>
            <p className="text-gray-500">
              Vos livraisons apparaîtront ici dès qu'elles vous seront assignées
            </p>
          </motion.div>
        )}

        {/* Empty state when offline */}
        {!isOnline && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Vous êtes hors ligne
            </h3>
            <p className="text-gray-500 mb-4">
              Passez en ligne pour recevoir vos livraisons assignées
            </p>
          </motion.div>
        )}

        {/* GPS Error */}
        {gpsError && isOnline && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl"
          >
            <p className="text-sm text-amber-800">
              <strong>GPS:</strong> {gpsError}. Activez la localisation pour une meilleure expérience.
            </p>
          </motion.div>
        )}
      </main>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

// Helper function for greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Bonjour';
  if (hour < 18) return 'Bon après-midi';
  return 'Bonsoir';
};

export default DashboardModern;
